// PIQ Advocate Engine - Opus 4.5 Build
// Advocate Profile Page - Detailed advocate view with score breakdown

import React, { useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { 
  ArrowLeft,
  User,
  ExternalLink,
  MessageSquare,
  DollarSign,
  Award,
  TrendingUp,
  Link2,
  Copy,
  Check,
  RefreshCw
} from 'lucide-react'
import { 
  useAdvocate, 
  useAdvocateScore,
  useMentions,
  useConversions,
  useCreateLink,
  useUpdateAdvocate,
  useRecalculateScores,
  useOffers,
  type ScoreComponents
} from '../../lib/advocate-opus-data-service'

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdvocateOpusProfile() {
  const [location, navigate] = useLocation()
  const id = useMemo(() => {
    const match = location.match(/\/advocate-opus\/advocates\/([^/?#]+)/)
    return match ? decodeURIComponent(match[1]) : null
  }, [location])
  
  const { data: advocate, isLoading: advocateLoading } = useAdvocate(id || null)
  const { data: score, isLoading: scoreLoading } = useAdvocateScore(id || null)
  const { data: mentions } = useMentions({ advocate_id: id, limit: 5 })
  const { data: conversions } = useConversions({ advocate_id: id, limit: 5 })
  const { data: offers } = useOffers(true)
  
  const createLink = useCreateLink()
  const updateAdvocate = useUpdateAdvocate()
  const recalculateScore = useRecalculateScores()
  
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [copiedLink, setCopiedLink] = useState<string | null>(null)

  if (advocateLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-5xl mx-auto animate-pulse space-y-6">
          <div className="h-8 w-32 bg-gray-200 rounded" />
          <div className="h-48 bg-gray-200 rounded-xl" />
        </div>
      </div>
    )
  }

  if (!advocate) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-5xl mx-auto text-center py-12">
          <p className="text-gray-500">Advocate not found</p>
          <button
            onClick={() => navigate('/advocate-opus/discovery')}
            className="mt-4 text-purple-600 hover:text-purple-700"
          >
            ← Back to Discovery
          </button>
        </div>
      </div>
    )
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return
    await updateAdvocate.mutateAsync({ advocateId: id, status: newStatus })
  }

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedLink(url)
    setTimeout(() => setCopiedLink(null), 2000)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-8 py-6">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/advocate-opus/discovery?view=advocates')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Advocates
          </button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {advocate.avatar_url ? (
                <img src={advocate.avatar_url} alt="" className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-8 h-8 text-gray-500" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">@{advocate.primary_handle}</h1>
                  <TierBadge tier={advocate.tier} />
                  <StatusBadge status={advocate.status} />
                </div>
                {advocate.display_name && (
                  <p className="text-gray-500">{advocate.display_name}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span className="capitalize">{advocate.primary_platform}</span>
                  {advocate.follower_count && (
                    <span>{advocate.follower_count.toLocaleString()} followers</span>
                  )}
                  {advocate.profile_url && (
                    <a
                      href={advocate.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-700"
                    >
                      View Profile <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => recalculateScore.mutate(id)}
                disabled={recalculateScore.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${recalculateScore.isPending ? 'animate-spin' : ''}`} />
                Recalculate Score
              </button>
              
              {advocate.status === 'new' && (
                <button
                  onClick={() => handleStatusChange('invited')}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Send Invite
                </button>
              )}
              
              {advocate.status === 'invited' && (
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Link2 className="w-4 h-4" />
                  Create Link
                </button>
              )}

              {advocate.status === 'active' && (
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Link2 className="w-4 h-4" />
                  Create Link
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-8 py-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            icon={Award}
            label="Score"
            value={advocate.score?.toFixed(0) || '0'}
            subtext={`Tier ${advocate.tier || 'C'}`}
          />
          <StatCard
            icon={MessageSquare}
            label="Total Mentions"
            value={advocate.total_mentions.toString()}
            subtext={`${advocate.positive_mentions || 0} positive`}
          />
          <StatCard
            icon={DollarSign}
            label="Revenue Generated"
            value={`$${(advocate.total_revenue_generated || 0).toLocaleString()}`}
            subtext="Net revenue"
          />
          <StatCard
            icon={TrendingUp}
            label="Commissions Paid"
            value={`$${(advocate.total_commissions_paid || 0).toLocaleString()}`}
            subtext="Total paid out"
          />
        </div>

        {/* Score Breakdown */}
        {score && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Score Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ScoreComponent
                name="Sentiment Strength"
                component={score.components.sentiment_strength}
              />
              <ScoreComponent
                name="Purchase Intent"
                component={score.components.purchase_intent}
              />
              <ScoreComponent
                name="Engagement Velocity"
                component={score.components.engagement_velocity}
              />
              <ScoreComponent
                name="Audience Credibility"
                component={score.components.audience_credibility}
              />
              <ScoreComponent
                name="Category Relevance"
                component={score.components.category_relevance}
              />
              <ScoreComponent
                name="Recency/Frequency"
                component={score.components.recency_frequency}
              />
            </div>
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <span className="text-gray-600">Mentions Analyzed: {score.mentions_analyzed}</span>
              <span className="text-lg font-bold">
                Total Score: {(score.score_total || 0).toFixed(0)}/100
              </span>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Mentions */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Mentions</h2>
              <button
                onClick={() => navigate(`/advocate-opus/discovery?advocate_id=${id}`)}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                View All →
              </button>
            </div>
            {mentions?.mentions.length ? (
              <div className="space-y-4">
                {mentions.mentions.map((m) => (
                  <div key={m.mention_id} className="border-b pb-4 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-700 line-clamp-2">{m.raw_text}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <span className="capitalize">{m.source_platform}</span>
                      {m.sentiment && (
                        <span className={`capitalize ${
                          m.sentiment === 'positive' ? 'text-green-600' : 
                          m.sentiment === 'negative' ? 'text-red-600' : ''
                        }`}>
                          {m.sentiment}
                        </span>
                      )}
                      <span>{m.published_at ? new Date(m.published_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No mentions yet</p>
            )}
          </div>

          {/* Recent Conversions */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Conversions</h2>
              <button
                onClick={() => navigate(`/advocate-opus/payouts?advocate_id=${id}`)}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                View All →
              </button>
            </div>
            {conversions?.conversions?.length ? (
              <div className="space-y-4">
                {conversions.conversions.map((c: any) => (
                  <div key={c.conversion_id} className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0">
                    <div>
                      <p className="font-medium text-gray-900">Order #{c.order_id.slice(0, 8)}...</p>
                      <p className="text-sm text-gray-500">
                        {new Date(c.order_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-600">${c.gross_revenue?.toLocaleString()}</p>
                      {c.refunded && (
                        <p className="text-xs text-red-500">Refunded: ${c.refund_amount}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No conversions yet</p>
            )}
          </div>
        </div>

        {/* Bio */}
        {advocate.bio && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Bio</h2>
            <p className="text-gray-600">{advocate.bio}</p>
          </div>
        )}
      </div>

      {/* Create Link Modal */}
      {showLinkModal && (
        <CreateLinkModal
          advocateId={id!}
          offers={offers?.offers || []}
          onClose={() => setShowLinkModal(false)}
          onCreated={(link) => {
            handleCopyLink(link)
            setShowLinkModal(false)
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StatCard({ icon: Icon, label, value, subtext }: {
  icon: React.ElementType
  label: string
  value: string
  subtext: string
}) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{subtext}</p>
        </div>
      </div>
    </div>
  )
}

function ScoreComponent({ name, component }: { 
  name: string
  component: { value: number; max: number; description: string } 
}) {
  const percentage = (component.value / component.max) * 100
  
  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{name}</span>
        <span className="text-sm font-bold">{component.value}/{component.max}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-purple-500 rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-2">{component.description}</p>
    </div>
  )
}

function TierBadge({ tier }: { tier: string | null }) {
  const colors: Record<string, string> = {
    A: 'bg-green-100 text-green-700',
    B: 'bg-yellow-100 text-yellow-700',
    C: 'bg-gray-100 text-gray-700',
  }
  
  if (!tier) return null
  
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded ${colors[tier] || colors.C}`}>
      Tier {tier}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    invited: 'bg-purple-100 text-purple-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-700',
    declined: 'bg-red-100 text-red-700',
  }
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${colors[status] || colors.new}`}>
      {status}
    </span>
  )
}

function CreateLinkModal({ advocateId, offers, onClose, onCreated }: {
  advocateId: string
  offers: Array<{ offer_id: string; offer_name: string; commission_rate: number }>
  onClose: () => void
  onCreated: (link: string) => void
}) {
  const createLink = useCreateLink()
  const [selectedOffer, setSelectedOffer] = useState<string>('')
  const [destinationUrl, setDestinationUrl] = useState<string>('https://example.com')

  const handleCreate = async () => {
    if (!selectedOffer || !destinationUrl) return
    
    const result = await createLink.mutateAsync({
      advocate_id: advocateId,
      offer_id: selectedOffer,
      destination_url: destinationUrl,
    })
    
    if (result.data?.full_tracking_url) {
      onCreated(result.data.full_tracking_url)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Attribution Link</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Offer</label>
            <select
              value={selectedOffer}
              onChange={(e) => setSelectedOffer(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Choose an offer...</option>
              {offers.map(o => (
                <option key={o.offer_id} value={o.offer_id}>
                  {o.offer_name} ({o.commission_rate}%)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
            <input
              type="url"
              value={destinationUrl}
              onChange={(e) => setDestinationUrl(e.target.value)}
              placeholder="https://your-store.com"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!selectedOffer || !destinationUrl || createLink.isPending}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {createLink.isPending ? 'Creating...' : 'Create Link'}
          </button>
        </div>
      </div>
    </div>
  )
}
